import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, slideInLeft, springs } from '../lib/animations';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import Strains from './Strains';
import HallOfShame from './HallOfShame';

interface LeaderboardEntry {
  roller_name: string;
  joints_rated: number;
  avg_score: number;
  best_score: number;
}

interface Badge {
  emoji: string;
  name: string;
  desc: string;
}

interface Rating {
  id: number;
  roll_consistency: number;
  shape_taper: number;
  filter_crutch: number;
  seal_quality: number;
  aesthetics: number;
  final_score: number;
  verdict: string;
  image_path: string;
  created_at: string;
}

interface TopJoint {
  roller_name: string;
  final_score: number;
  verdict: string;
  image_path: string;
  created_at: string;
}

type SubTab = 'rankings' | 'strains' | 'hall-of-shame';

const subTabs: { key: SubTab; label: string }[] = [
  { key: 'rankings', label: 'Rankings' },
  { key: 'strains', label: 'Strains' },
  { key: 'hall-of-shame', label: 'Hall of Shame' },
];

export default function Leaderboard() {
  const { toasts, addToast } = useToast();
  const [activeTab, setActiveTab] = useState<SubTab>('rankings');
  const [data, setData] = useState<{ leaderboard: LeaderboardEntry[]; topJoint: TopJoint | null; badges: Record<string, Badge[]> }>({ leaderboard: [], topJoint: null, badges: {} });
  const [selectedRoller, setSelectedRoller] = useState<string | null>(null);
  const [history, setHistory] = useState<Rating[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(getApiUrl('/api/leaderboard'))
      .then(r => r.json())
      .then(setData)
      .catch((err) => {
        console.error('Failed to load leaderboard:', err);
        setError('Failed to load leaderboard data');
        addToast('Failed to load leaderboard', 'error');
      })
      .finally(() => setLoading(false));
  }, [addToast]);

  const showHistory = async (name: string) => {
    if (selectedRoller === name) { setSelectedRoller(null); return; }
    setSelectedRoller(name);
    setHistoryLoading(true);
    try {
      const res = await fetch(`/api/ratings/${encodeURIComponent(name)}`);
      if (!res.ok) throw new Error('Failed to load ratings');
      setHistory(await res.json());
    } catch (err) {
      console.error('Failed to load roller history:', err);
      addToast(`Failed to load ratings for ${name}`, 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const rollerBadges = (name: string) => data.badges?.[name] || [];

  return (
    <div className="max-w-4xl mx-auto px-3 py-4">
      <h2 className="text-xl font-bold text-cream mb-3">Leaderboard</h2>

      {/* Error state */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Sub-tab navigation */}
      <div className="flex gap-1.5 mb-4">
        {subTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              activeTab === tab.key
                ? 'bg-warm/80 text-cannabis-950'
                : 'bg-cannabis-800 text-cream/45 hover:text-cream hover:bg-cannabis-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Rankings tab */}
      {activeTab === 'rankings' && (
        <div className="max-w-2xl">
          {loading ? (
            <p className="text-cream/40 text-center py-8">Loading leaderboard...</p>
          ) : error ? (
            <p className="text-cream/40 text-center py-8">Unable to load leaderboard</p>
          ) : (
            <>
              {data.topJoint && (
            <div className="bg-cannabis-800 rounded-2xl p-3.5 border border-warm/20 mb-4">
              <div className="flex items-start gap-3">
                {data.topJoint.image_path && (
                  <img src={data.topJoint.image_path} alt="Top joint" className="w-16 h-16 object-cover rounded-xl" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-warm font-bold text-sm">Top Joint of All Time</span>
                  </div>
                  <p className="text-cream/45 text-xs">
                    By <Link to={`/profile/${encodeURIComponent(data.topJoint.roller_name)}`} className="text-cream hover:text-cannabis-300 transition">{data.topJoint.roller_name}</Link> — Score: <span className="text-cannabis-400 font-mono">{data.topJoint.final_score}</span>
                  </p>
                  <p className="text-cream/50 italic text-xs mt-0.5 truncate">"{data.topJoint.verdict}"</p>
                </div>
              </div>
            </div>
          )}

          {data.leaderboard.length === 0 ? (
            <p className="text-cream/40 text-center py-6 text-sm">No ratings yet. Be the first to rate a joint!</p>
          ) : (
            <div className="bg-cannabis-800 rounded-2xl border border-cannabis-700/30 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-cannabis-700/50 text-cream/40">
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Roller</th>
                    <th className="px-3 py-2 text-center">Rated</th>
                    <th className="px-3 py-2 text-center">Avg</th>
                    <th className="px-3 py-2 text-center">Best</th>
                  </tr>
                </thead>
                <motion.tbody variants={staggerContainer} initial="initial" animate="animate">
                  {data.leaderboard.map((entry, i) => (
                    <React.Fragment key={entry.roller_name}>
                      <motion.tr
                        variants={slideInLeft}
                        className="border-b border-cannabis-700/30 hover:bg-cannabis-700/20 cursor-pointer transition"
                        onClick={() => showHistory(entry.roller_name)}
                        style={i < 3 ? { boxShadow: i === 0 ? '0 0 12px rgba(212,165,116,0.15)' : i === 1 ? '0 0 8px rgba(192,192,192,0.1)' : '0 0 8px rgba(205,127,50,0.1)' } : undefined}
                      >
                        <td className="px-3 py-2 font-mono">
                          {i + 1}
                        </td>
                        <td className="px-3 py-2 font-semibold text-cream">
                          <Link
                            to={`/profile/${encodeURIComponent(entry.roller_name)}`}
                            className="hover:text-cannabis-300 transition"
                            onClick={e => e.stopPropagation()}
                          >
                            {entry.roller_name}
                          </Link>
                          {rollerBadges(entry.roller_name).map((b, j) => (
                            <span key={j} className="ml-0.5 cursor-help text-[11px]" title={`${b.name}: ${b.desc}`}>{b.emoji}</span>
                          ))}
                          {i === data.leaderboard.length - 1 && data.leaderboard.length > 1 && (
                            <span className="ml-0.5 cursor-help text-[11px] text-red-400" title="Lowest scoring roller">Lowest</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-cream/50">{entry.joints_rated}</td>
                        <td className="px-3 py-2 text-center text-cannabis-400 font-mono">{entry.avg_score}</td>
                        <td className="px-3 py-2 text-center text-cannabis-300 font-mono">{entry.best_score}</td>
                      </motion.tr>
                      <AnimatePresence>
                      {selectedRoller === entry.roller_name && (
                        <motion.tr
                          key={`${entry.roller_name}-history`}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={springs.subtle}
                        >
                          <td colSpan={5} className="px-3 py-2 bg-cannabis-900/50">
                            {history.length === 0 ? (
                              <p className="text-cream/40 text-center py-1 text-xs">No history</p>
                            ) : (
                              <div className="space-y-2">
                                {history.map(r => (
                                  <div key={r.id} className="flex items-start gap-2 bg-cannabis-800/50 rounded-xl p-2.5">
                                    {r.image_path && (
                                      <img src={r.image_path} alt="" className="w-12 h-12 object-cover rounded-lg" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <span className="text-cannabis-400 font-mono font-bold text-xs">{r.final_score.toFixed(2)}</span>
                                        <span className="text-cream/25 text-[10px]">{new Date(r.created_at).toLocaleDateString()}</span>
                                      </div>
                                      <p className="text-cream/50 italic text-[11px] mt-0.5 line-clamp-2">"{r.verdict}"</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </motion.tr>
                      )}
                      </AnimatePresence>
                    </React.Fragment>
                  ))}
                </motion.tbody>
              </table>
            </div>
          )}
            </>
          )}
        </div>
      )}
      <Toast toasts={toasts} />

      {/* Strains tab */}
      {activeTab === 'strains' && <Strains />}

      {/* Hall of Shame tab */}
      {activeTab === 'hall-of-shame' && <HallOfShame />}
    </div>
  );
}
